import React from 'react';
import { Box, Collapse, Divider, Typography } from '@mui/material';

import { Alert } from '../../../Alert';
import { Button } from '../../../Button';
import { TextInput, YesNoRadioButtonGroup } from '../../../Inputs';
import { ScrollableView, FullscreenFormContainer, FullscreenFormFooterContainer } from '../../../Layout';
import { CollapseWidth } from '../../../Transitions';
import { CancelButton, FormikFieldValueChangeHandler, SubmitButton } from '../../../Form';

import { useL10n } from '@uplift/hooks';
import { Formik } from 'formik';
import { CalendarSubscribeToICSError, calendarSubscribeToICS, isApiError } from '@uplift/api';
import { useAlert } from '../../../AlertProvider';
import { GoogleCalendarICSInstruction } from './GoogleCalendarICSInstruction.component';
import { ICSUrlVerification } from './ICSUrlVerification.component';


export const SubscribeGoogleCalendarForm: React.FC<{
  onBack?: () => void;
  onSkip?: () => void;
  onSuccess: (ics?: { title: string }) => void;
  nextButtonTitle?: string;
  showFUETitle?: boolean;
  style?: React.CSSProperties;
  disabled?: boolean;
  enableNoOptionTip?: boolean;
}> = ({ showFUETitle = true, enableNoOptionTip = true, ...props }) => {
  const alert = useAlert();
  const l10n = useL10n();

  return (
    <Formik
      initialValues={{ wantToConnect: null, calendarUrl: '', verification: null }}
      onSubmit={async (values) => {
        try {
          if (values.wantToConnect && values.calendarUrl) {
            const response = await calendarSubscribeToICS(values.calendarUrl);

            props.onSuccess(response.data);
          } else {
            props.onSuccess();
          }
        } catch (err) {
          if (isApiError<CalendarSubscribeToICSError>(err)) {
            const data = err.response?.data;

            if (data) {
              if ('error' in data) {
                alert.error(data.error || err.message);
              } else if ('fields' in data) {
                alert.error(data.message || err.message);
              }
            } else {
              alert.error(err.message);
            }
          } else {
            throw err;
          }
        }
      }}
      validate={async (values) => {
        if (values.wantToConnect) {
          if (!values.calendarUrl) {
            return {
              calendarUrl: ' ',
            };
          }

          if (values.calendarUrl && !values.verification) {
            return {
              calendarUrl: ' ',
            };
          }
        } else if (values.wantToConnect === null) {
          return {
            wantToConnect: ' ',
          };
        }

        return {};
      }}
    >
      {formik => (
        <Box display="flex" flex="1" height="100%">
          <FullscreenFormContainer>
            <Box style={props.style}>
              {showFUETitle && (
                <Box>
                  <Typography variant="overline" color="secondary">{l10n.schedule.configuration.calendarIntegration.setYourRecurringAvailability}</Typography>
                </Box>
              )}
              <Box mt={2}>
                <Typography variant="h4">{l10n.schedule.configuration.calendarIntegration.doYouWantToConnect}</Typography>
              </Box>
              <Box mt={1}>
                <Typography variant="body1" color="textSecondary">{l10n.schedule.configuration.calendarIntegration.youWillBeAbleToSeeYourEvents}</Typography>
              </Box>
              <Box mt={2}>
                <YesNoRadioButtonGroup asBoolean name="wantToConnect" label="" />
                <FormikFieldValueChangeHandler
                  fieldName="wantToConnect"
                  onChange={(newValue) => {
                    if (newValue === false) {
                      formik.setFieldValue('calendarUrl', null, true);
                      formik.setFieldTouched('calendarUrl', false, false);
                    }
                  }}
                />
              </Box>
              <Collapse in={!!formik.values.wantToConnect}>
                <Box pt={3}>
                  <Box bgcolor="grey.100" p={3}>
                    <TextInput name="calendarUrl" label="Google Calendar URL" fullWidth color="secondary" />
                  </Box>
                  <Box mt={3}>
                    <Collapse in={!!formik.values.calendarUrl}>
                      <ICSUrlVerification
                        url={formik.values.calendarUrl}
                        onStatusChange={valid => formik.setFieldValue('verification', valid)}
                      />
                    </Collapse>
                  </Box>
                </Box>
              </Collapse>
              {enableNoOptionTip && (
                <Collapse in={formik.values.wantToConnect === false}>
                  <Box pt={8}>
                    <Alert severity="info" title="Tip">
                      You can always connect a Google Calendar later from your UpLift Calendar settings.
                    </Alert>
                  </Box>
                </Collapse>
              )}
              <Box mt={8}>
                <FullscreenFormFooterContainer
                  actionsLeft={props.onBack && (
                    <CancelButton size="large" onClick={props.onBack}>Back</CancelButton>
                  )}
                  actionsRight={(
                    <>
                      {props.onSkip && <Button size="large" disabled={props.disabled || formik.isSubmitting} onClick={props.onSkip}>do this later</Button>}
                      <SubmitButton size="large" color="secondary">
                        {props.nextButtonTitle || 'Finish setup'}
                      </SubmitButton>
                    </>
                  )}
                />
              </Box>
            </Box>
          </FullscreenFormContainer>
          <CollapseWidth in={!!formik.values.wantToConnect && !props.disabled && !formik.isSubmitting && !formik.isValid} width="466px">
            <Box height="100%" width="466px" display="flex">
              <Box>
                <Divider orientation="vertical" />
              </Box>
              <ScrollableView width="100%">
                <GoogleCalendarICSInstruction />
              </ScrollableView>
            </Box>
          </CollapseWidth>
        </Box>
      )}
    </Formik>
  );
};
